import { TextInput } from '@amelio/ui-library';
import { useState } from 'react';

import type { TextInputProps } from '@amelio/ui-library';

import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { formatEditableNumber, parseEditableNumber } from '@/utils/numberFormat';

interface NumberInputProps
    extends Omit<
        TextInputProps,
        'type' | 'inputMode' | 'hideNativeSteppers' | 'value' | 'onChange' | 'onBlur'
    > {
    value: number;
    onChange: (value: number) => void;
    /** Receives the number the field settles on once left. */
    onBlur?: (value: number) => void;
}

interface Edit {
    text: string;
    valueBefore: number;
}

/**
 * A numeric field bound to a number, read and written with the decimal separator of the app
 * language rather than the browser's: a native number field reads "82,5" as 825 in an English
 * browser. The field shows what is typed and passes on numbers only; left empty or on text that is
 * not a number, it goes back to the value it had before the edit instead of turning into 0.
 */
export const NumberInput = ({ value, onChange, onBlur, ...inputProps }: NumberInputProps) => {
    const language = useSelectedAppLanguage();
    const [edit, setEdit] = useState<Edit | null>(null);

    const handleChange = (text: string) => {
        setEdit({ text, valueBefore: edit?.valueBefore ?? value });
        const typedValue = parseEditableNumber(text, language);
        if (typedValue !== null) {
            onChange(typedValue);
        }
    };

    const handleBlur = () => {
        const isUnreadable = edit !== null && parseEditableNumber(edit.text, language) === null;
        const settledValue = isUnreadable ? edit.valueBefore : value;
        if (settledValue !== value) {
            onChange(settledValue);
        }
        setEdit(null);
        onBlur?.(settledValue);
    };

    return (
        <TextInput
            {...inputProps}
            inputMode={'decimal'}
            value={edit?.text ?? formatEditableNumber(value, language)}
            onChange={handleChange}
            onBlur={handleBlur}
        />
    );
};
